import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../Spinner';
import { getHelpAreaLabel, getHelpArticlePath } from './helpConstants';

export default function HelpArticleList({ articles, loading, error, emptyMessage }) {
  const [search, setSearch] = useState('');

  const groupedArticles = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    const filtered = (articles || []).filter((article) => {
      if (!normalized) return true;
      return [article.title, article.summary, article.area, getHelpAreaLabel(article.area)]
        .join(' ')
        .toLowerCase()
        .includes(normalized);
    });
    return filtered.reduce((groups, article) => {
      const area = article.area || 'general';
      if (!groups[area]) groups[area] = [];
      groups[area].push(article);
      return groups;
    }, {});
  }, [articles, search]);

  const areas = Object.keys(groupedArticles);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Could not load help articles. Try refreshing the page.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative">
        <label htmlFor="help-article-search" className="sr-only">Search help articles</label>
        <input
          id="help-article-search"
          type="search"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search by title or area…"
          className="h-11 w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-gray-900 placeholder-gray-500 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
        />
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {areas.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-600">
          {emptyMessage || 'No help articles are available for your role yet.'}
        </div>
      ) : (
        areas.map((area) => (
          <section key={area} className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-white lg:text-gray-500">
              {getHelpAreaLabel(area)}
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {groupedArticles[area].map((article) => (
                <Link
                  key={article.slug}
                  to={getHelpArticlePath(article)}
                  className="block rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-sky-300 hover:bg-sky-50"
                >
                  <h3 className="text-base font-semibold text-gray-900">{article.title}</h3>
                  {article.summary ? (
                    <p className="mt-1 text-sm text-gray-600">{article.summary}</p>
                  ) : null}
                </Link>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
